import { Box, Typography } from "@mui/material";
import {
  apiRequest,
  catchErrorMessageHandler,
  getUserSelectedRole,
  isPastDate,
} from "../../../../infrastructure/backendService";
import direction from "../../../../assets/direction.svg";
import invoice from "../../../../assets/invoice.svg";
import ErrorPopup from "../../../common/ErrorPopup";
import toast from "react-hot-toast";
import apiPath from "../../../../config/apiPaths.json";
import constants from "../../../../config/constants.json";
import { useState } from "react";
import InvoiceDialog from "../../../common/Invoice/InvoiceDialog";

const styles = {
  pooja_booked_time: {
    color: "#F05802",
    fontSize: { xs: "10px", md: "14px" },
    fontFamily: "Lato",
    fontWeight: "600",
    lineHeight: 1.4,
    wordWrap: "break-word",
    cursor: "pointer",
    width: "fit-content",
    textDecoration: "underline",
  },
  pooja_name: {
    color: "#313131",
    fontSize: { xs: "10px", md: "14px" },
    fontFamily: "Lato",
    fontWeight: "400",
    lineHeight: 1.4,
    wordWrap: "break-word",
  },
  pooja_date: {
    color: "#882E14",
    fontSize: { xs: "10px", md: "14px" },
    fontFamily: "Lato",
    fontWeight: "400",
    lineHeight: 1.4,
    wordWrap: "break-word",
  },
};

interface SchedulingBarProps {
  handleClickAdd: (pooja?: any) => void;
  priestSchedulesData: any;
  isOnlyBar?: boolean;
  isOnlyDisplay?: boolean;
}

const SchedulingBar = (props: SchedulingBarProps) => {
  const { handleClickAdd, priestSchedulesData, isOnlyBar, isOnlyDisplay } = props;

  const [invoiceData, setInvoiceData] = useState<any>(null);

  const dayTimeSlots = [
    "06 AM",
    "07 AM",
    "08 AM",
    "09 AM",
    "10 AM",
    "11 AM",
    "12 PM",
    "01 PM",
    "02 PM",
    "03 PM",
    "04 PM",
    "05 PM",
    "06 PM",
    "07 PM",
    "08 PM",
    "09 PM",
  ];

  function calculateTimePercentage(startTime: any, endTime: any) {
    // Convert start and end times to minutes
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
    const endMinutes = endDate.getHours() * 60 + endDate.getMinutes();

    const totalMinutes = endMinutes - startMinutes;

    const percentage = totalMinutes / (16 * 60);

    return percentage;
  }

  function calculateStartTimePercentage(startTime: any) {
    // Convert start and end times to minutes
    const startDate = new Date(startTime);
    const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();

    const percentage = (startMinutes - 6 * 60) / (16 * 60);

    return percentage * 100;
  }

  const getBackgroundColor = (each: any) => {
    if (isOnlyBar) {
      return "#F05802";
    } else {
      let color = "blue";
      if (each?.poojaName?.toLowerCase() === "day off" || each?.weekOff) {
        color = "red";
      } else if (each?.devoteeDetails) {
        color = "maroon";
        if (each?.outsidePooja) {
          color = "orange";
        }
      }

      return color;
    }
  };

  const handleOpenGoogleMaps = (devoteeDetails: any) => {
    const fullAddress = `${devoteeDetails?.address}, ${devoteeDetails?.city}, ${devoteeDetails?.state}, ${devoteeDetails?.zipcode}`;
    const baseUrl = "https://www.google.com/maps/search/?api=1";
    const query = `&query=${encodeURIComponent(fullAddress)}`;
    const googleMapsUrl = `${baseUrl}${query}`;
    window.open(googleMapsUrl, "_blank"); // Open in a new tab
  };

  const handleOpenInvoice = async (
    scheduleId: any,
    priestName: string,
    startTime: string,
    endTime: string
  ) => {
    await apiRequest({
      method: "GET",
      endpoint: `${apiPath.schedules}/${scheduleId}`,
      includeToken: true,
    })
      .then((res) => {
        setInvoiceData({
          ...res?.data,
          open: true,
          priestName,
          startTime,
          endTime,
          startDate: priestSchedulesData?.date?.date,
        });
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const getIsManagerOrDevoteePooja = (poojaDetails: any) => {
    return getUserSelectedRole() === "Manager" || poojaDetails.devoteeDetails;
  };

  const handleUpdateSchedule = (poojaDetails: any) => {
    if (getIsManagerOrDevoteePooja(poojaDetails)) {
      handleClickAdd({ ...poojaDetails, isModifyOrDelete: true });
    }
  };

  const renderScheduledPooja = (each: any) => {
    return (
      <Box
        sx={{
          width: `${calculateTimePercentage(
            `04/04/2024 ${each.startTime}`,
            `04/04/2024 ${each.endTime}`
          )}`,
          left: `${calculateStartTimePercentage(
            `04/04/2024 ${each.startTime}`
          )}%`,
          position: "absolute",
          backgroundColor: `${getBackgroundColor(each)}`,
          borderRadius: `${isOnlyBar ? "8px" : "4px"}`,
          height: `${isOnlyBar ? "100%" : "calc(100% - 8px)"}`,
          my: `${isOnlyBar ? "0px" : "4px"}`,
        }}
      ></Box>
    );
  };

  const renderTimeBar = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            mb: "4px",
            justifyContent: "space-between",
          }}
        >
          {dayTimeSlots?.map((time, index) => (
            <Box width={`calc(100% / ${dayTimeSlots?.length})`}>
              <Typography
                sx={{
                  fontSize: { xs: "8px", sm: "10px" },
                  fontFamily: "Lato",
                  fontWeight: 500,
                  color: "black",
                  textAlign: "left",
                }}
                key={index}
              >
                |{time}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            backgroundColor: `${isOnlyBar ? "#22A69999" : "#22A699"}`,
            width: "calc(100%)",
            height: `${isOnlyBar ? "15px" : "25px"}`,
            borderRadius: `${isOnlyBar ? "8px" : "4px"}`,
            position: "relative",
          }}
        >
          {priestSchedulesData?.schedules?.map((each: any) => {
            return renderScheduledPooja(each);
          })}
        </Box>
      </Box>
    );
  };

  const getDevoteeDetails = (devoteeDetails: any) => {
    const name = `| ${devoteeDetails?.firstname} ${devoteeDetails?.lastname}`;
    const address =
      devoteeDetails?.city && devoteeDetails?.state
        ? `(${devoteeDetails?.city} ${devoteeDetails?.state})`
        : "";
    return `${name} ${address}`;
  };

  const renderBookedPoojaDetails = () => {
    return (
      <Box sx={{ width: "100%", pt: "11px" }}>
        {!isPastDate(priestSchedulesData?.date?.date) && !isOnlyDisplay && (
          <Typography
            sx={{ ...styles.pooja_booked_time }}
            onClick={() =>
              handleClickAdd({
                isModifyOrDelete: false,
                date: priestSchedulesData?.date?.date,
              })
            }
            className="no_print"
          >
            [Add]
          </Typography>
        )}
        {priestSchedulesData?.schedules?.map((pooja: any, index: number) => {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: `${
                  index !== priestSchedulesData?.schedules?.length - 1
                    ? "6px"
                    : "0px"
                }`,
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
              key={index}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: { xs: "column", md: "row" },
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{
                    ...styles.pooja_booked_time,
                    textDecoration: `${
                      getIsManagerOrDevoteePooja(pooja) && !isOnlyDisplay ? "underline" : "none"
                    }`,
                    cursor: `${
                      getIsManagerOrDevoteePooja(pooja) && !isOnlyDisplay ? "pointer" : "initial"
                    }`,
                  }}
                  onClick={() => handleUpdateSchedule(pooja)}
                >
                  {pooja?.startTime?.toUpperCase()} -{" "}
                  {pooja?.endTime.toUpperCase()}
                </Typography>
                <Typography
                  sx={{ ...styles.pooja_name, ml: { xs: "0px", md: "5px" } }}
                >
                  {pooja?.poojaName}{" "}
                  <Box
                    component="span"
                    sx={{ ...styles.pooja_name, color: "red" }}
                  >
                    ({pooja?.createdBy}, {pooja?.createdDate})
                  </Box>{" "}
                  {pooja?.devoteeDetails &&
                    getDevoteeDetails(pooja?.devoteeDetails)}
                </Typography>
              </Box>
              {!isOnlyDisplay && <Box
                sx={{ display: "flex", alignItems: "center" }}
                className="no_print"
              >
                {pooja?.outsidePooja && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      mr: "10px",
                    }}
                    onClick={() => handleOpenGoogleMaps(pooja?.devoteeDetails)}
                  >
                    <Box
                      component={"img"}
                      src={direction}
                      alt="Get Direction"
                      sx={{ width: "16px", height: "16px" }}
                    />
                    <Typography
                      sx={{
                        display: { xs: "none", sm: "inline" },
                        color: "#43A022",
                        fontSize: { sm: "12px", md: "14px" },
                        fontFamily: "Lato",
                        fontWeight: "600",
                        lineHeight: "1.4",
                        wordWrap: "break-word",
                        ml: "5px",
                      }}
                    >
                      Get Direction
                    </Typography>
                  </Box>
                )}
                {pooja?.devoteeDetails && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      handleOpenInvoice(
                        pooja?.scheduleId,
                        priestSchedulesData?.priestOrHallName,
                        pooja?.startTime?.toUpperCase(),
                        pooja?.endTime.toUpperCase()
                      )
                    }
                  >
                    <Box
                      component={"img"}
                      src={invoice}
                      alt="Invoice"
                      sx={{ width: "16px", height: "16px" }}
                    />
                    <Typography
                      sx={{
                        display: { xs: "none", sm: "inline" },
                        color: "#43A022",
                        fontSize: { sm: "12px", md: "14px" },
                        fontFamily: "Lato",
                        fontWeight: "600",
                        lineHeight: "1.4",
                        wordWrap: "break-word",
                        ml: "5px",
                      }}
                    >
                      Invoice
                    </Typography>
                  </Box>
                )}
              </Box>}
            </Box>
          );
        })}
      </Box>
    );
  };
  return (
    <>
      {renderTimeBar()}
      {!isOnlyBar && (
        <>
          {renderBookedPoojaDetails()}
          <InvoiceDialog
            open={invoiceData?.open}
            handleClose={() => setInvoiceData(null)}
            poojaBookingDetails={invoiceData}
          />
        </>
      )}
    </>
  );
};

export default SchedulingBar;
